import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { PercentField } from "@/shared/components/PercentField";
import {
	applyPercentEdit,
	getDrainOrder,
	isAllocationTotalWithinLimit,
} from "@/shared/utils/allocationSplit";
import { ErrorModal, HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import { Grid2, Tooltip, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { type GasAllocationTarget } from "../models/GasAllocationTarget";
import { upsertGasAllocationDefaults } from "../repositories/GasAllocationsRepository";

type Props = {
	targets: GasAllocationTarget[];
	modal: "update" | "";
	onHide: () => void;
};

export const EditGasDefaultsModal = ({ targets, modal, onHide }: Props) => {
	const queryClient = useQueryClient();

	const [percentByCode, setPercentByCode] = useState<
		Record<string, number | null>
	>({});
	const [percentOrder, setPercentOrder] = useState<string[]>([]);
	const [error, setError] = useState("");

	const targetCodes = targets.map((target) => target.TargetCode);
	const fieldsPerRow =
		targets.length > 4 ? Math.ceil(targets.length / 2) : targets.length;

	const upsertMutation = useMutation(upsertGasAllocationDefaults, {
		meta: { successMessage: "The default shares were updated successfully" },
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["gas-allocation-targets"],
			});
			onHide();
		},
		onError: (error: Error) => setError(error.message),
	});

	const total = targetCodes.reduce(
		(sum, code) => sum + (percentByCode[code] ?? 0),
		0,
	);
	const isTotalValid = isAllocationTotalWithinLimit(total);

	const lastEditedCode = percentOrder[percentOrder.length - 1] ?? null;
	const balancingCode =
		lastEditedCode === null
			? null
			: (getDrainOrder(targetCodes, lastEditedCode, percentOrder)[0] ?? null);

	const onPercentChange = (code: string, value: number | null) => {
		const nextOrder = [...percentOrder.filter((other) => other !== code), code];
		setPercentOrder(nextOrder);
		setPercentByCode((current) =>
			applyPercentEdit(current, targetCodes, code, value, nextOrder),
		);
	};

	const onSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (!isTotalValid) return;

		upsertMutation.mutate(
			targets.map((target) => ({
				TargetId: target.TargetId,
				DefaultAllocationPercent: percentByCode[target.TargetCode] ?? 0,
			})),
		);
	};

	useEffect(() => {
		if (modal === "") return;

		const calculatedPercentByCode: Record<string, number | null> = {};

		for (const target of targets)
			calculatedPercentByCode[target.TargetCode] =
				target.DefaultAllocationPercent;

		setPercentByCode(calculatedPercentByCode);
		setPercentOrder([]);
	}, [modal, targets]);

	return (
		<>
			{modal === "update" && (
				<HelmetDexteel title="Edit Default Split - Gas Allocations" />
			)}
			<MesfModal
				id="modal-gas-allocations"
				title="EDIT DEFAULT SPLIT"
				open={modal !== ""}
				handleClose={onHide}
				maxWidth="sm"
			>
				<form onSubmit={onSubmit}>
					<MesfModal.Content style={{ padding: "15px 30px" }} dividers>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12 }}>
								<Typography variant="body2" color="textSecondary">
									The share each target starts a new day from on the Gas Reading
									screen. A day already entered keeps the shares it was saved
									with.
								</Typography>
							</Grid2>

							{targets.map((target) => {
								const isBalancing = balancingCode === target.TargetCode;

								return (
									<Grid2
										key={target.TargetId}
										size={{ md: 12 / fieldsPerRow, xs: 12 }}
									>
										<Tooltip
											title={
												isBalancing
													? "First to give up % when the split goes over 100%"
													: ""
											}
											placement="top"
										>
											<span>
												<PercentField
													label={target.TargetName}
													value={percentByCode[target.TargetCode] ?? null}
													onChange={(value) =>
														onPercentChange(target.TargetCode, value)
													}
													fullWidth
													autoComplete="off"
													size="small"
													margin="none"
													sx={
														isBalancing
															? { "& input": { color: "text.secondary" } }
															: undefined
													}
												/>
											</span>
										</Tooltip>
									</Grid2>
								);
							})}

							<Grid2 size={{ xs: 12 }}>
								<Typography
									variant="body2"
									color={isTotalValid ? "textSecondary" : "error"}
									data-test-id="gas-defaults-total"
									sx={{ fontWeight: 600, textAlign: "right" }}
								>
									{`Total ${total.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`}
									{isTotalValid ? "" : " — cannot go over 100%"}
								</Typography>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={upsertMutation.isPending}
							onHide={onHide}
							buttonLabel="Save"
							buttonColor="primary"
							disabled={!isTotalValid}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
